/**
 * Syncreel Video.js wrapper component.
 *
 * Exposes the exact cross-workstream surface declared in the master build
 * prompt §5.3: {@link VideoPlayerHandle}, {@link VideoPlayer}, and
 * {@link VideoPlayerProps}. WS-5's `RoomPage` renders this and forwards the
 * handle to WS-8's `useRoomSync`.
 */
// WHY: the quality plugins (videojs-contrib-quality-levels /
// the quality-levels plugin) are imported ONCE, in useVideoJs.ts, where
// they are also called early (before VHS parses the manifest) so the quality
// menu populates. Importing them here as well re-runs `videojs.registerPlugin`
// in some bundler/HMR paths and triggers "A plugin named 'qualityLevels'
// already exists" — so this file must NOT import them. They are plugins (not
// components), so they do not belong in registerComponents.ts either.
import { useImperativeHandle, useMemo, type JSX, type Ref } from 'react';
import { type MediaDetail, type Role } from '@syncreel/shared';

import { HOST_CONTROL_BAR, VIEWER_CONTROL_BAR } from './controlBarConfig';
import { registerComponents } from './registerComponents';
import { useVideoJs, type Player, type PlayerOptions } from './useVideoJs';
// WHY: WS-6 owns player.css; importing it here bundles the dark-theme skin
// overrides and custom-control layout with the player component itself.
import '../styles/player.css';
import '../styles/player-skin.css';

// Register custom components + video.js CSS once at module load. Idempotent
// (guarded against HMR / StrictMode re-evaluation), so calling at import time
// is safe and keeps side effects out of the render body.
registerComponents();

/** The Video.js player instance type, re-exported for consumers (WS-8). */
export type { Player };

/** Imperative handle used by the sync engine (WS-8) to reach the player. */
export interface VideoPlayerHandle {
  /** The live Video.js player, or `null` before init / after dispose. */
  player(): Player | null;
  /** The underlying `<video>` element, or `null` before init / after dispose. */
  videoEl(): HTMLVideoElement | null;
}

/** Props for {@link VideoPlayer}. */
export interface VideoPlayerProps {
  /** Media to play; a new `media` identity re-initialises the player. */
  media: MediaDetail;
  /** Role drives the control-bar layout (host vs viewer). */
  role: Role;
  /** Called once when the player is ready and quality plugins are wired. */
  onReady(player: Player): void;
  /**
   * When true, the player stretches to fill its container's full height
   * (used by the fullscreen+chat layout) instead of maintaining a 16:9
   * aspect ratio via video.js `fluid` mode. Implemented purely in CSS so
   * toggling it never re-initialises the player (changing the `fluid`/
   * `fill` options would dispose + recreate the player via useVideoJs).
   */
  fill?: boolean;
}

/**
 * Build memoisable Video.js options for a given media + role.
 *
 * Sources use the FFmpeg-generated HLS master manifest. `overrideNative`
 * forces VHS on browsers with native HLS (desktop Safari) so track/quality
 * menus are consistent. `crossOrigin` is applied to the media element so
 * WS-7's Web Audio delay graph gets a CORS-clean source. Hotkeys are
 * disabled so removing the viewer scrub bar is sufficient to prevent
 * transport control.
 */
function buildOptions(media: MediaDetail, role: Role): PlayerOptions {
  const children = role === 'host' ? [...HOST_CONTROL_BAR] : [...VIEWER_CONTROL_BAR];
  return {
    sources: [{ src: media.manifestUrl, type: 'application/vnd.apple.mpegurl' }],
    html5: { vhs: { overrideNative: true } },
    preload: 'auto',
    playsinline: true,
    fluid: true,
    controls: true,
    crossOrigin: 'anonymous',
    userActions: { hotkeys: false },
    controlBar: { children },
  };
}

/**
 * Add external WebVTT subtitle tracks to the player as remote text tracks.
 * The tracks are served as plain .vtt files under /hls/<mediaId>/subs/<lang>.vtt.
 */
function setupSubtitles(player: Player, media: MediaDetail): void {
  for (const track of media.subtitleTracks) {
    // WHY: build the full URL from the relative playlistUrl. The HLS base
    // is /hls/<mediaId>/, and playlistUrl is e.g. "subs/eng.vtt".
    const src = `/hls/${media.id}/${track.playlistUrl}`;
    try {
      (player as unknown as {
        addRemoteTextTrack(o: {
          kind: string;
          src: string;
          label?: string;
          language?: string;
        }): unknown;
      }).addRemoteTextTrack({
        kind: 'subtitles',
        src,
        label: track.label,
        language: track.language,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to add subtitle track', src, err);
    }
  }
}

/**
 * Syncreel Video.js player. Forwards a {@link VideoPlayerHandle} via ref.
 *
 * The player is re-initialised only when the memoised options identity
 * changes (driven by `media` / `role`). `onReady` is forwarded through a
 * ref inside {@link useVideoJs}, so a changing callback never tears down
 * the player.
 */
export function VideoPlayer(props: VideoPlayerProps & { ref?: Ref<VideoPlayerHandle> }): JSX.Element {
  const { media, role, onReady, fill, ref } = props;

  const options = useMemo<PlayerOptions>(() => buildOptions(media, role), [media, role]);

  const { containerRef, playerRef } = useVideoJs({
    options,
    onReady: (player) => {
      // WHY: quality plugins are initialised in useVideoJs before VHS parses
      // the manifest (see setupQualityEarly). Here we only add subtitles and
      // notify the parent that the player is ready.
      setupSubtitles(player, media);
      onReady(player);
    },
  });

  useImperativeHandle(
    ref,
    (): VideoPlayerHandle => ({
      player: () => playerRef.current,
      videoEl: () => {
        const player = playerRef.current;
        if (!player) return null;
        return player.el().querySelector('video') as HTMLVideoElement | null;
      },
    }),
    [playerRef],
  );

  return (
    <div
      ref={containerRef}
      className={`syncreel-player${fill ? ' syncreel-player--fill' : ''}`}
    />
  );
}
